import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, DollarSign, Clock, Star, Search, Download, Plus, Mail, Phone, MessageSquare, FileText, CreditCard, BarChart3, MapPin, Calendar, Eye, X, Filter } from 'lucide-react';
import OwnerDetail from './OwnerDetail';

const OwnersManagement = () => {
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showOwnerDetail, setShowOwnerDetail] = useState(false);
  const [currentOwnerDetail, setCurrentOwnerDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showNewOwnerModal, setShowNewOwnerModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  // Statistiques principales
  const stats = {
    activeOwners: 127,
    newThisQuarter: 12,
    exclusiveOwners: 68,
    nonExclusiveOwners: 59,
    satisfaction: 89,
    avgDuration: 3.2,
    totalProperties: 247,
    portfolioValue: "€485M",
    villasCount: 156,
    yachtsCount: 89,
    occupancyRate: 87,
    avgRevenue: "€1.9M",
    commissionsSept: "€312K",
    totalReservations: "€2.4M",
    avgCommissionRate: 13,
    reversedAmount: "€245K",
    transactionsCount: 47,
    paymentsUpToDate: 98,
    upcomingPayments: "€1.8M",
    next30Days: "€485K",
    thisWeek: "€485K",
    ownersCount: 23,
    latePayments: 5,
    avgDelayDays: 3
  };

  // Propriétaires principaux
  const mainOwners = [
    {
      id: 'OWN-2025-001',
      initials: 'FB',
      name: 'Famille Beaumont',
      type: 'Exclusif PRIVIA',
      since: '2022',
      propertiesCount: 5,
      portfolioValue: '€18.5M',
      properties: ['Villa Azure Dreams', 'Villa Sunset Paradise'],
      locations: ['St-Barthélemy', 'St-Tropez'],
      revenueYTD: '€2.4M',
      commissionPrivia: '€312K',
      nextPayment: '15 Sept',
      amountDue: '€245,000',
      status: 'active'
    },
    {
      id: 'OWN-2025-012',
      initials: 'MV',
      name: 'Mediterranean Villas Ltd',
      type: 'Société',
      since: '2021',
      propertiesCount: 12,
      portfolioValue: '€45M',
      properties: ['Portfolio Mykonos (8)', 'Portfolio Sardaigne (4)'],
      locations: ['Grèce', 'Italie'],
      revenueYTD: '€4.8M',
      commissionPrivia: '€480K',
      occupancyRate: 92,
      amountDue: '€420,000',
      status: 'maintenance'
    },
    {
      id: 'OWN-2025-008',
      initials: 'JR',
      name: 'Jean-Pierre Rousseau',
      type: 'Particulier',
      since: '2020',
      propertiesCount: 2,
      portfolioValue: '€12M',
      properties: ['Villa Paradiso', 'Villa Belle Vue'],
      locations: ['St-Tropez', 'Cannes'],
      revenueYTD: '€1.2M',
      commissionPrivia: '€180K',
      clientSatisfaction: 96,
      amountDue: '€145,000',
      status: 'active'
    }
  ];

  // Fonctions de gestion
  const handleOwnerClick = (owner) => {
    setCurrentOwnerDetail(owner);
    setShowOwnerDetail(true);
  };

  const handleBackFromDetail = () => {
    setShowOwnerDetail(false);
    setCurrentOwnerDetail(null);
  };

  const handleSaveOwnerDetail = (updatedOwner) => {
    // Logique de sauvegarde
    console.log('Saving owner:', updatedOwner);
  };

  const handleExportExcel = () => {
    // Logique d'export Excel
    alert('Export Excel en cours...');
  };

  const handleNewOwner = () => {
    setShowNewOwnerModal(true);
  };

  const handleFilters = () => {
    setShowFiltersModal(true);
  };

  const handlePayment = (owner) => {
    alert(`Traitement du paiement de ${owner.amountDue} pour ${owner.name}`);
  };

  const handleGenerateReport = (owner) => {
    alert(`Génération du rapport pour ${owner.name}`);
  };

  // Transactions de commissions
  const commissionTransactions = [
    {
      id: 'OWN-2025-001',
      owner: 'Famille Beaumont',
      property: 'Villa Azure Dreams',
      location: 'St-Barthélemy',
      reservation: '#PRV2587',
      client: 'Davidson',
      totalAmount: '€280,000',
      commission: '€36,400',
      commissionRate: 13,
      netAmount: '€243,600',
      dueDate: '15 Sept 2025',
      status: 'scheduled'
    },
    {
      id: 'OWN-2025-012',
      owner: 'Mediterranean Villas',
      property: 'Villa Celestial',
      location: 'Mykonos',
      reservation: '#PRV2586',
      client: 'Chen',
      totalAmount: '€175,000',
      commission: '€17,500',
      commissionRate: 10,
      netAmount: '€157,500',
      dueDate: '18 Sept 2025',
      status: 'pending'
    },
    {
      id: 'OWN-2025-008',
      owner: 'Jean-Pierre Rousseau',
      property: 'Villa Paradiso',
      location: 'St-Tropez',
      reservation: '#PRV2585',
      client: 'Al-Rashid',
      totalAmount: '€450,000',
      commission: '€67,500',
      commissionRate: 15,
      netAmount: '€382,500',
      dueDate: '10 Sept 2025',
      status: 'paid'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'paid': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const OwnerDetailsPanel = () => {
    if (!showDetailsPanel || !selectedOwner) return null;

    const owner = mainOwners.find(o => o.id === selectedOwner);
    if (!owner) return null;

    return (
      <div className="fixed inset-y-0 right-0 w-1/3 bg-white border-l border-gray-200 shadow-xl z-40 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Détails Propriétaire</h3>
            <button
              onClick={() => setShowDetailsPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Informations Propriétaire */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold">
                {owner.initials}
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{owner.name}</h4>
                <p className="text-sm text-gray-600">{owner.type} • Membre depuis {owner.since}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">{owner.portfolioValue}</div>
                <div className="text-sm text-gray-600">Valeur Portfolio</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">{owner.revenueYTD}</div>
                <div className="text-sm text-gray-600">Revenue Total</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">{owner.occupancyRate || 92}%</div>
                <div className="text-sm text-gray-600">Taux Occupation</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">4.8/5</div>
                <div className="text-sm text-gray-600">Note Clients</div>
              </div>
            </div>
          </div>

          {/* Contrats Actifs */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Contrats Actifs</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Contrat Exclusif PRIVIA</span>
                <span className="font-medium text-green-600">Actif</span>
              </div>
              <div className="text-xs text-gray-500">
                • Durée: 3 ans (2022-2025)<br/>
                • Commission: 13% standard, 10% long séjour<br/>
                • Exclusivité sur {owner.propertiesCount} propriétés<br/>
                • Renouvellement automatique: Oui
              </div>
            </div>
          </div>

          {/* Portfolio Propriétés */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Portfolio Propriétés ({owner.propertiesCount})</h4>
            <div className="space-y-3">
              {owner.properties.map((property, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium text-sm text-gray-800">{property}</div>
                    <div className="text-xs text-gray-600">{owner.locations[index]} • 6 chambres</div>
                  </div>
                  <span className="text-xs font-medium text-green-600">Active</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Actions</h4>
            
            <div className="space-y-2">
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                <CreditCard size={16} />
                <span>Procéder au Paiement ({owner.amountDue})</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                <BarChart3 size={16} />
                <span>Générer Rapport Performance</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                <Mail size={16} />
                <span>Envoyer Communication</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-yellow-600 border border-yellow-300 rounded-lg hover:bg-yellow-50">
                <FileText size={16} />
                <span>Renouveler Contrat</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal Nouveau Propriétaire
  const NewOwnerModal = () => {
    if (!showNewOwnerModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Nouveau Propriétaire</h3>
            <button
              onClick={() => setShowNewOwnerModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom/Société</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Nom du propriétaire"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>Particulier</option>
                  <option>Société</option>
                  <option>Exclusif PRIVIA</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="email@exemple.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Première propriété</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Nom de la propriété"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>St-Barthélemy</option>
                  <option>St-Tropez</option>
                  <option>Mykonos</option>
                  <option>Sardaigne</option>
                  <option>Mustique</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taux de commission (%)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="13"
                  min="5"
                  max="25"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                  placeholder="Notes internes..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowNewOwnerModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                alert('Propriétaire créé avec succès !');
                setShowNewOwnerModal(false);
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Créer le Propriétaire
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal Filtres
  const FiltersModal = () => {
    if (!showFiltersModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Filtres avancés</h3>
            <button
              onClick={() => setShowFiltersModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut du propriétaire</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option>Tous</option>
                <option>Actif</option>
                <option>Maintenance</option>
                <option>Suspendu</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valeur du portfolio</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option>Toutes valeurs</option>
                <option>Moins de €5M</option>
                <option>€5M - €20M</option>
                <option>€20M - €50M</option>
                <option>Plus de €50M</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Taux d'occupation</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option>Tous</option>
                <option>Moins de 70%</option>
                <option>70% - 85%</option>
                <option>Plus de 85%</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ancienneté</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option>Toutes</option>
                <option>Moins de 1 an</option>
                <option>1-3 ans</option>
                <option>Plus de 3 ans</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowFiltersModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Réinitialiser
            </button>
            <button
              onClick={() => setShowFiltersModal(false)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Appliquer Filtres
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Si on affiche la page de détail
  if (showOwnerDetail && currentOwnerDetail) {
    return (
      <OwnerDetail 
        owner={currentOwnerDetail}
        onBack={handleBackFromDetail}
        onSave={handleSaveOwnerDetail}
      />
    );
  }

  return (
    <div className="owners-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion Propriétaires & Commissions</h1>
            <p className="text-gray-600 mt-1">Suivi des propriétaires, portfolio et reversements de commissions</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button 
              onClick={handleFilters}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter size={18} />
              <span>Filtres</span>
            </button>
            <button 
              onClick={handleExportExcel}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
            >
              <Download size={18} />
              <span>Export Excel</span>
            </button>
            <button 
              onClick={handleNewOwner}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <Plus size={18} />
              <span>Nouveau Propriétaire</span>
            </button>
          </div>
        </div>

        {/* Grid de Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          {/* Propriétaires Actifs */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-center mb-2">
              <div className="text-2xl font-bold text-gray-800">{stats.activeOwners}</div>
              <div className="text-sm text-gray-600">Propriétaires Actifs</div>
              <div className="text-xs text-green-600 font-medium">+{stats.newThisQuarter} ce trimestre</div>
            </div>
            <div className="flex justify-between items-center text-xs">
              <div className="text-center">
                <div className="font-bold text-blue-600">{stats.exclusiveOwners}</div>
                <div className="text-gray-600">Exclusifs</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-orange-600">{stats.nonExclusiveOwners}</div>
                <div className="text-gray-600">Non-exclusifs</div>
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-sm font-medium text-green-600">{stats.satisfaction}% Satisfaction</div>
              <div className="text-xs text-gray-600">{stats.avgDuration} ans Durée moy.</div>
            </div>
          </div>

          {/* Propriétés Portfolio */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-center mb-2">
              <div className="text-2xl font-bold text-gray-800">{stats.totalProperties}</div>
              <div className="text-sm text-gray-600">Propriétés Portfolio</div>
              <div className="text-xs text-green-600 font-medium">Valeur totale: {stats.portfolioValue}</div>
            </div>
            <div className="flex justify-between items-center text-xs">
              <div className="text-center">
                <div className="font-bold text-green-600">{stats.villasCount}</div>
                <div className="text-gray-600">Villas</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-blue-600">{stats.yachtsCount}</div>
                <div className="text-gray-600">Yachts</div>
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-sm font-medium text-green-600">{stats.occupancyRate}% Taux occupation</div>
              <div className="text-xs text-gray-600">{stats.avgRevenue} Rev. moyen</div>
            </div>
          </div>

          {/* Commissions Sept 2025 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-center mb-2">
              <div className="text-2xl font-bold text-gray-800">{stats.commissionsSept}</div>
              <div className="text-sm text-gray-600">Commissions Sept 2025</div>
              <div className="text-xs text-gray-600">Sur {stats.totalReservations} de réservations</div>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Taux moyen</span>
                <span className="font-medium">{stats.avgCommissionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Reversés</span>
                <span className="font-medium text-green-600">{stats.reversedAmount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transactions</span>
                <span className="font-medium">{stats.transactionsCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">À jour</span>
                <span className="font-medium text-green-600">{stats.paymentsUpToDate}%</span>
              </div>
            </div>
          </div>

          {/* Paiements à Venir */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-center mb-2">
              <div className="text-2xl font-bold text-gray-800">{stats.upcomingPayments}</div>
              <div className="text-sm text-gray-600">Paiements à Venir</div>
              <div className="text-xs text-gray-600">Prochains 30 jours</div>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Cette semaine</span>
                <span className="font-medium text-purple-600">{stats.thisWeek}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Propriétaires</span>
                <span className="font-medium">{stats.ownersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">En retard</span>
                <span className="font-medium text-red-600">{stats.latePayments}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Délai moyen</span>
                <span className="font-medium">J+{stats.avgDelayDays}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous les propriétaires</option>
              <option value="exclusive">Exclusifs PRIVIA</option>
              <option value="multi-agency">Multi-agences</option>
              <option value="new">Nouveaux (&lt;3 mois)</option>
              <option value="vip">VIP (&gt;€5M portfolio)</option>
            </select>

            <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
              <option>Toutes destinations</option>
              <option>St-Barthélemy</option>
              <option>St-Tropez</option>
              <option>Mykonos</option>
              <option>Sardaigne</option>
              <option>Mustique</option>
            </select>

            <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
              <option>Statut paiement</option>
              <option>À jour</option>
              <option>Paiement en attente</option>
              <option>Retard &gt;7 jours</option>
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher propriétaire..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
          </div>
        </div>

        {/* Propriétaires Principaux */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">Propriétaires Principaux</h3>
          
          <div className="space-y-6">
            {mainOwners.map((owner) => (
              <div key={owner.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold">
                      {owner.initials}
                    </div>
                    <div>
                      <h4 
                        className="font-semibold text-gray-800 cursor-pointer hover:text-yellow-600 transition-colors duration-200"
                        onClick={() => handleOwnerClick(owner)}
                      >
                        {owner.name}
                      </h4>
                      <p className="text-sm text-gray-600">{owner.type} • Depuis {owner.since}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-800">{owner.propertiesCount}</div>
                      <div className="text-xs text-gray-600">Propriétés</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-800">{owner.portfolioValue}</div>
                      <div className="text-xs text-gray-600">Valeur</div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePayment(owner)}
                        className="px-3 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                      >
                        <CreditCard size={14} className="inline mr-1" />
                        Paiement
                      </button>
                      <button 
                        onClick={() => handleGenerateReport(owner)}
                        className="px-3 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50"
                      >
                        <BarChart3 size={14} className="inline mr-1" />
                        Rapport
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-gray-800">Revenue YTD</div>
                    <div className="text-gray-600">{owner.revenueYTD}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">Commission PRIVIA</div>
                    <div className="text-gray-600">{owner.commissionPrivia}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">{owner.occupancyRate ? 'Taux occupation' : 'Prochain paiement'}</div>
                    <div className="text-gray-600">{owner.occupancyRate ? `${owner.occupancyRate}%` : owner.nextPayment}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">À verser</div>
                    <div className="font-medium text-green-600">{owner.amountDue}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tableau des Commissions */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Détail des Commissions & Reversements</h3>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setShowFiltersModal(true)}
                  className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Filtrer
                </button>
                <button 
                  onClick={() => alert('Traitement des paiements en cours...')}
                  className="px-3 py-1 text-sm text-green-600 border border-green-300 rounded hover:bg-green-50"
                >
                  Traiter Paiements
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriétaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriété
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Réservation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net à Verser
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissionTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{transaction.owner}</div>
                      <div className="text-sm text-gray-500">ID: {transaction.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.property}</div>
                      <div className="text-sm text-gray-500">{transaction.location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.reservation}</div>
                      <div className="text-sm text-gray-500">{transaction.client}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {transaction.totalAmount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.commission}</div>
                      <div className="text-sm text-gray-500">{transaction.commissionRate}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {transaction.netAmount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.dueDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                        {transaction.status === 'scheduled' ? 'Programmé' : 
                         transaction.status === 'pending' ? 'En attente' : 
                         transaction.status === 'paid' ? 'Payé' : transaction.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => alert(`Détails pour la transaction ${transaction.reservation}`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {transaction.status === 'paid' ? 'Reçu' : 'Détails'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <OwnerDetailsPanel />

      {/* Modales */}
      <NewOwnerModal />
      <FiltersModal />
    </div>
  );
};

export default OwnersManagement;